import { Routes, Route, Outlet, useLocation } from "react-router-dom";
import SiteHeader from "./Components/SiteHeader";
import Footer from "./Components/Footer";
import SearchSection from "./Components/SearchSection";
import HeartlandWaySection from "./Components/HeartlandWaySection";
import WhereWeServe from "./Components/WhereWeServe";
import RentOutPropertySection from "./Components/RentOutPropertySection";
import FAQsSection from "./Components/FAQs";
import FeaturedPropertiesSection from "./Components/FeaturedProperties";
import TrackRecord from "./Components/TrackRecord";
import ReactFullpage from "@fullpage/react-fullpage";

import Availability from "./Components/Availability";
import { Link } from "react-router-dom";
import KansasCountiesMap from "./Components/KansasCountiesMap";
import Hero from "./Components/Hero";
import Owners from "./Pages/Owners";
import { OwnerHero } from "./Components/OwnerHero";
import {
  ArrowRightIcon,
  CheckCircleIcon,
  CheckIcon,
} from "@heroicons/react/20/solid";
import KansasCountiesInteractive from "./Components/KansasCountiesMap";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Input,
} from "@headlessui/react";
import { PlusSmallIcon } from "@heroicons/react/20/solid";
import { MinusSmallIcon } from "@heroicons/react/20/solid";
import { useEffect, useState } from "react";
import ThankYou from "./Pages/ThankYou";
import PrivacyPolicy from "./PrivacyPolicy";
import TermsOfService from "./TermsOfService";
const faqs = [
  {
    question: "How do I pay my rent?",
    answer:
      "You can easily pay your rent through your Tenant Portal, available online or in our mobile app.",
  },
  {
    question: "What should I do if I need maintenance?",
    answer:
      "All maintenance requests should be submitted through your Tenant Portal, available online or in our mobile app. For urgent but non-life-threatening issues, please call our team 24/7 at 785-338-0502. For life-threatening emergencies, dial 911 immediately.",
  },
  {
    question: "How long does it take to process a rental application?",
    answer:
      "Rental applications are processed in the order they are received. You can typically expect a response within 24 hours of applying during the week.",
  },
  {
    question: "Do you allow pets in your properties?",
    answer:
      "Each property has its own pet policy. Please ask your leasing officer for the details on the property you’re interested in. If you are a current tenant, please inquire about the pet policy of your property before getting a pet.",
  },
  {
    question: "What happens when my lease ends?",
    answer:
      "Within 30 to 60 days of your lease ending, our team will contact you to discuss renewal. If you plan to vacate, a 30-day notice is required and can be submitted by call, text, or email.",
  },
];

// Keep your existing HomePage content but REMOVE header/footer here
// function HomePage() {
//   return (
//     <>
//       {/* HERO SECTION (unchanged except it no longer includes header/footer) */}
//       <Hero />

//       {/* Rest of homepage sections */}
//       {/* <SearchSection /> */}
//       <HeartlandWaySection />
//       <WhereWeServe />
//       {/* <FeaturedPropertiesSection /> */}
//       <TrackRecord />
//       <FAQsSection />
//       <RentOutPropertySection />
//     </>
//   );
// }
function HomePage() {
  const location = useLocation();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    document.title = "Heartland – Managing Your Property The Right Way.";
  }, []);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();

    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.question,
      acceptedAnswer: { "@type": "Answer", text: f.answer },
    })),
  };

  return (
    <div>
      <ReactFullpage
        licenseKey={"N1CG6-QB45H-EKCRI-0HXQ7-OWZAL"}
        scrollingSpeed={200}
        anchors={[
          "home",
          "features",
          "whereWeServe",
          "aboutUs",
          "faqs",
          "contactUs",
        ]}
        scrollOverflow={true}
        verticalCentered={true}
        recordHistory={false}
        lockAnchors={true}
        scrollHorizontally={false}
        credits={{ enabled: false }}
        responsiveWidth={760}
        afterRender={() => {
          if (window.fullpage_api) {
            // Only move to the first section if no hash is present
            if (!window.location.hash) {
              window.fullpage_api.moveTo(1);
            }
          }
        }}
        render={({ state, fullpageApi }) => {
          return (
            <ReactFullpage.Wrapper>
              {/* SECTION 1 — HERO (flex only) */}
              {/* ✅ CORRECT: This is the ONLY element that should have the 'section' class here. */}
              <section className="section relative mb-8">
                {/* Full-width background image */}
                <div className="absolute inset-0 w-full h-full">
                  <img
                    src="/suburban-home-at-sunset.png"
                    alt="Street view of brick home at Sunset."
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60" />
                </div>

                {/* Content container (still centered, but now overlays full-width image) */}
                <div
                  className="relative z-10 mt-20 mx-auto px-6 md:px-10
      max-w-[1200px] md:max-w-[1280px]
      lg:max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1720px]"
                >
                  <div className="flex items-center py-14 lg:py-18">
                    <div className="">
                      <h1 className="text-5xl md:text-6xl xl:text-7xl font-extrabold text-white leading-[1.1]">
                        Managing your property <br />
                        <span className="text-[#d1d1d1]">the right way.</span>
                      </h1>

                      <p className="font-medium mt-5 text-lg md:text-xl text-white max-w-2xl">
                        Where tenants feel at home, and owners have peace of
                        mind.
                      </p>

                      <div className="flex flex-wrap gap-4 mt-8">
                        <a
                          href="https://heartlandpropertysolutions.appfolio.com/connect"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center rounded-md border border-black bg-[#F9DC5C] px-8 py-3 text-md font-semibold text-brand-black hover:translate-y-[1px] transition"
                        >
                          Pay Rent
                        </a>

                        <a
                          href="/availability"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center rounded-md border border-black bg-white px-8 py-3 text-md font-semibold text-brand-black hover:translate-y-[1px] transition"
                        >
                          Availability
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <section className="section">
                <div
                  className="md:mt-20 mx-auto px-6 lg:px-10
      max-w-[1200px] md:max-w-[1280px]
      lg:max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1720px]"
                >
                  <div className="h-auto lg:h-[40rem] 2xl:h-[52rem] flex flex-col lg:flex-row items-stretch gap-6">
                    {/* LEFT */}
                    <article className="relative overflow-hidden rounded-2xl flex lg:flex-[1.3]">
                      <img
                        src="/woman-paying-rent-on-her-phone.jpg"
                        alt="Woman in yellow sweater sitting on a couch, smiling at her phone while paying rent."
                        className="absolute inset-0 h-full w-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/65" />
                      <div className="relative z-10 w-full p-6 sm:p-8 md:p-10 lg:p-12 flex flex-col items-start justify-end">
                        <p className="text-xs md:text-[14px] font-semibold uppercase tracking-wider text-white/80">
                          Online Payments
                        </p>
                        <h3 className="mt-2 text-2xl sm:text-6xl font-extrabold leading-snug text-white max-w-xl">
                          Pay your rent online. <br />
                        </h3>
                        <h3
                          className="md:mt-3 text-2xl sm:text-6xl font-extrabold leading-snug max-w-xl"
                          style={{ color: "#d1d1d1" }}
                        >
                          Any time, anywhere.
                        </h3>
                        <a
                          href="/availability"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="mt-6 inline-flex items-center gap-2 self-start rounded-lg border border-black bg-[#F9DC5C] px-6 py-3 text-sm font-semibold text-black hover:brightness-95 transition"
                        >
                          Check Availability
                          <ArrowRightIcon className="h-4 w-4" />
                        </a>
                      </div>
                    </article>

                    {/* RIGHT */}
                    <article className="rounded-3xl overflow-hidden flex lg:flex-[0.7] bg-gradient-to-b from-[#F9DC5C] via-[#FCEB8A] to-[#FFF9D7]">
                      <div className="m-auto w-full p-6 sm:p-10">
                        {/* Label */}
                        <p className="text-[11px] font-semibold uppercase tracking-wider text-brand-black/70">
                          Tenant Portal
                        </p>

                        {/* Title */}
                        <h4 className="mt-2 text-2xl sm:text-3xl font-extrabold leading-snug text-brand-black">
                          All-in-one access to your rental tools.
                        </h4>

                        {/* Feature List */}
                        <div className="mt-8 space-y-5">
                          {[
                            "Pay Rent",
                            "Submit & track maintenance requests",
                            "View & download lease documents",
                            "Communicate with property manager",
                            "Electronic Signatures",
                          ].map((feature, idx) => (
                            <div
                              key={idx}
                              className="flex items-start gap-4 rounded-lg bg-black/5 p-4 hover:bg-black/10 transition"
                            >
                              <CheckCircleIcon
                                className="h-6 w-6 text-[#1E2A38] mt-1"
                                aria-hidden="true"
                              />
                              <p className="text-brand-black text-base font-semibold">
                                {feature}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </article>
                  </div>
                </div>
              </section>

              <section
                className={`${!isMobile ? "section" : "py-16"} md:w-full bg-[#FCFBF6]`}
                id="whereWeServe"
              >
                <div
                  className="md:mt-20 mx-auto px-6 lg:px-10
      max-w-[1200px] md:max-w-[1280px]
      lg:max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1720px]
      text-center"
                >
                  {/* <div className="h-[42rem] 2xl:h-[52rem] bg-[#FCFBF6] rounded-xl p-14"> */}
                  <div className="md:h-[40rem] 2xl:h-[52rem] bg-[#FCFBF6] rounded-xl">
                    {/* Heading + Text */}
                    <h2 className="text-4xl md:text-5xl font-extrabold text-[#2B2B2B]">
                      Where we serve
                    </h2>
                    <p className="mt-1 text-md text-[#333] max-w-2xl mx-auto">
                      Proudly serving{" "}
                      <span className="font-semibold">
                        central and eastern Kansas.
                      </span>
                    </p>

                    <Link
                      to="/owners"
                      className="mt-6 inline-flex items-center justify-center rounded-md bg-white border border-gray-300 px-6 py-2.5 font-semibold text-brand-black hover:scale-[1.02] hover:shadow-lg transition"
                    >
                      Manage My Property →
                    </Link>
                    {/* CTA */}

                    {/* Map */}
                    <div className="mt-6 flex justify-center">
                      <div className="w-full md:max-w-[85%]">
                        <KansasCountiesInteractive
                          url="/data/ks-counties.geojson"
                          countyNameField="NAME"
                          primary="#F9DC5C"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section className="section w-full bg-[#1E2A38]" id="aboutUs">
                <div
                  className="mx-auto px-6 lg:px-10
                max-w-[1200px] md:max-w-[1280px]
                lg:max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1720px]"
                >
                  <div className="py-8 md:py-0 md:h-[40rem] 2xl:h-[52rem] md:mt-28 flex flex-col md:flex-row items-center md:items-stretch">
                    {/* LEFT: Text + Stats */}
                    <div className="flex-1 flex flex-col justify-center text-white">
                      <p className="text-sm font-semibold text-white">
                        Our Track Record
                      </p>
                      <h2 className="mt-2 text-4xl md:text-5xl font-extrabold leading-snug">
                        Here’s how we’re doing
                      </h2>
                      <p className="mt-4 text-white max-w-xl">
                        In five years, our team has grown to manage more than
                        $15M in assets across central and eastern Kansas. We owe
                        our success to a clear focus on serving both owners and
                        tenants while embracing technology to deliver smarter,
                        more efficient property management.
                      </p>

                      {/* Stats */}
                      <div className="mt-8 flex flex-wrap gap-x-5 md:gap-x-12 gap-y-8">
                        <div>
                          <p className="text-2xl font-bold">250</p>
                          <p className="text-sm text-white">total doors</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">170,000</p>
                          <p className="text-sm text-white">sq ft</p>
                        </div>
                        <div>
                          <p className="text-2xl font-bold">$15M</p>
                          <p className="text-sm text-white">
                            total assets managed
                          </p>
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="mt-3">
                        <Link
                          to="/owners"
                          className="w-full md:w-[15rem] mt-6 flex items-center justify-center gap-2 rounded-lg bg-[#F9DC5C] px-6 py-3 font-semibold text-brand-black hover:scale-[1.02] hover:shadow-lg transition"
                        >
                          Manage My Property
                          <ArrowRightIcon className="h-4 w-4" />
                        </Link>
                      </div>
                    </div>

                    {/* RIGHT: Image */}
                    <div className="flex-1 flex">
                      <img
                        src="/front-door-icons.png"
                        alt="Front door with icons for faucet, keys, and contract, representing home buying."
                        className=" md:w-[40rem] object-contain"
                      />
                    </div>
                  </div>
                </div>
              </section>
              <section
                aria-labelledby="faqs-title"
                className={`${!isMobile ? "section" : "py-16"}`}
              >
                <div
                  className="mx-auto px-6 lg:px-10
                              max-w-[1200px] md:max-w-[1280px]
                              lg:max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1720px] h-full"
                >
                  <div>
                    <h2
                      id="faqs-title"
                      className="text-4xl font-bold tracking-tight text-brand-black sm:text-5xl"
                    >
                      Frequently asked questions
                    </h2>

                    <dl className="mt-10 divide-y divide-gray-300/40">
                      {faqs.map((faq) => (
                        <Disclosure
                          key={faq.question}
                          as="div"
                          className="py-5 first:pt-0 last:pb-0"
                        >
                          <dt>
                            <DisclosureButton className="group flex w-full items-start justify-between text-left text-brand-black">
                              <span className="text-base font-semibold">
                                {faq.question}
                              </span>
                              <span className="ml-4 flex h-7 items-center">
                                <PlusSmallIcon
                                  aria-hidden="true"
                                  className="size-6 group-data-[open]:hidden"
                                />
                                <MinusSmallIcon
                                  aria-hidden="true"
                                  className="size-6 hidden group-data-[open]:block"
                                />
                              </span>
                            </DisclosureButton>
                          </dt>
                          <DisclosurePanel as="dd" className="mt-2 pr-10">
                            <p className="text-base text-gray-700">
                              {faq.answer}
                            </p>
                          </DisclosurePanel>
                        </Disclosure>
                      ))}
                    </dl>
                  </div>
                </div>

                {/* JSON-LD for SEO (safe to remove if you don't want it) */}
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify(faqJsonLd),
                  }}
                />
              </section>
              <section className="section w-full bg-[#1E2A38]">
                <div
                  className="mx-auto px-3 lg:px-10
      max-w-[1200px] md:max-w-[1280px]
      lg:max-w-[1400px] xl:max-w-[1600px] 2xl:max-w-[1720px]"
                >
                  <div className="px-3 py-8 md:p-20 md:h-[42rem] 2xl:h-[52rem] md:mt-16 flex flex-col md:flex-row items-center items-stretch gap-8">
                    {/* LEFT: Heading + List */}
                    <div className="flex-1 flex flex-col justify-center text-white">
                      <h2 className="text-4xl font-extrabold leading-tight md:text-6xl">
                        Want to rent out
                        <br />{" "}
                        <span className="text-[#d1d1d1]">your property?</span>
                      </h2>

                      <p className="mt-6 text-white/80 max-w-lg">
                        Let Heartland take the stress out of investing.
                        <br />
                        Contact us today to get started.
                      </p>

                      <ul className="mt-6 space-y-5">
                        {[
                          "Competitive Pricing",
                          "Leasing Services & Tenant Screening",
                          "Complete Financial Reporting",
                          "In Person Showings",
                        ].map((item) => (
                          <li key={item} className="flex items-center gap-3">
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#F9DC5C]">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 text-[#1E2A38]"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={3}
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                            </span>
                            <span className="text-md font-medium">{item}</span>
                          </li>
                        ))}
                      </ul>

                      <a
                        href="#learn-more"
                        className="mt-8 inline-flex items-center gap-2 font-semibold text-white underline underline-offset-4 hover:text-[#F9DC5C] transition"
                      >
                        Learn More →
                      </a>
                    </div>

                    {/* RIGHT: Form */}
                    <div className="flex-1 flex items-center justify-center">
                      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-md flex flex-col justify-center">
                        <form
                          action="https://formsubmit.co/bpotsmiket@gmail.com"
                          method="POST"
                          className="space-y-3"
                        >
                          {/* Redirect after submission */}
                          <input
                            type="hidden"
                            name="_next"
                            value="http://localhost:3001/thank-you"
                          />

                          {/* Disable captcha (optional) */}
                          <input type="hidden" name="_captcha" value="true" />

                          {/* First Name */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              First Name
                            </label>
                            <input
                              type="text"
                              name="firstName"
                              required
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Last Name */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Last Name
                            </label>
                            <input
                              type="text"
                              name="lastName"
                              required
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Phone Number */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Phone Number
                            </label>
                            <input
                              type="text"
                              name="phone"
                              required
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Work Email */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Work Email
                            </label>
                            <input
                              type="email"
                              name="email"
                              required
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Location */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Location
                            </label>
                            <input
                              type="text"
                              name="location"
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Units */}
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Number of Units
                            </label>
                            <input
                              type="number"
                              name="units"
                              className="w-full rounded-md bg-gray-50 border border-gray-300 px-4 py-2.5 text-sm 
                  focus:outline-none focus:ring-2 focus:ring-[#F9DC5C]"
                            />
                          </div>

                          {/* Submit Button */}
                          <button
                            type="submit"
                            className="w-full rounded-md bg-[#F9DC5C] py-3 font-semibold text-black 
                hover:brightness-95 transition"
                          >
                            Let’s Talk!
                          </button>
                        </form>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <Footer />
            </ReactFullpage.Wrapper>
          );
        }}
      />
    </div>
  );
}

// Shared layout
function Layout() {
  return (
    <div className="font-poppins relative">
      <SiteHeader />
      <Outlet />
    </div>
  );
}

export default function App() {
  const location = useLocation();
  return (
    <Routes location={location} key={location.pathname}>
      <Route element={<Layout />}>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/availability" element={<Availability />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/owners" element={<Owners />} /> {/* new route */}
      </Route>
    </Routes>
  );
}
